import React, { useEffect, useState, useCallback } from 'react';
import { apiGet, apiDownload } from '../../lib/api';
import { FileSpreadsheet, AlertTriangle } from 'lucide-react';
import { formatINR } from '../../lib/format';

export default function TDSRegister() {
  const [range, setRange] = useState({ from: '', to: '' });
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (range.from) params.from = range.from;
    if (range.to) params.to = range.to;
    apiGet('/finance/tds-register', params).then(setData).catch(() => setData({ items: [], totals: {}, pan_missing_count: 0 }));
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const exportExcel = () => {
    const qs = new URLSearchParams({ format: 'excel', ...(range.from ? { from: range.from } : {}), ...(range.to ? { to: range.to } : {}) });
    apiDownload(`/finance/tds-register?${qs.toString()}`, `tds_register_${range.from || 'all'}_${range.to || 'all'}.xlsx`);
  };

  const items = data?.items || [];

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="tds-register-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Finance & Accounts</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">TDS Register</h1>
          <p className="text-sm text-slate-600 mt-1">TDS deducted across RA bills, vendor payments and payroll — Form 26Q reference.</p>
        </div>
        <button onClick={exportExcel} data-testid="tds-export-btn" className="px-4 py-2 bg-[#1f6e43] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#185536] flex items-center gap-2"><FileSpreadsheet size={14} /> Export Excel</button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <input type="date" className="border border-slate-200 rounded-sm px-3 py-1.5 text-xs" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="tds-from" />
        <span className="text-slate-400 text-xs">to</span>
        <input type="date" className="border border-slate-200 rounded-sm px-3 py-1.5 text-xs" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="tds-to" />
      </div>

      {data?.pan_missing_count > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800 flex items-center gap-2" data-testid="pan-warning">
          <AlertTriangle size={14} /> {data.pan_missing_count} row(s) are missing PAN — required for Form 26Q filing. Update the party/employee PAN.
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 max-w-md" data-testid="tds-totals">
        <div className="rounded-sm border border-slate-200 p-3 bg-white"><div className="text-[10px] text-slate-500 font-bold uppercase">Gross</div><div className="text-sm font-display font-extrabold text-[#3b4872] mt-1">{formatINR(data?.totals?.gross_amount || 0)}</div></div>
        <div className="rounded-sm border border-slate-200 p-3 bg-white"><div className="text-[10px] text-slate-500 font-bold uppercase">Total TDS</div><div className="text-sm font-display font-extrabold text-[#3b4872] mt-1">{formatINR(data?.totals?.tds_amount || 0)}</div></div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500"><tr>{['Direction', 'Party', 'PAN', 'Section', 'Gross', 'Rate %', 'TDS', 'Period'].map((h) => <th key={h} className="text-left px-3 py-2 font-display font-bold uppercase tracking-wider text-[10px]">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 ? <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">No TDS in range.</td></tr> : items.map((r, i) => (
              <tr key={i} data-testid={`tds-row-${i}`}>
                <td className="px-3 py-2 text-slate-600">{r.direction.replace(/_/g, ' ')}</td>
                <td className="px-3 py-2 font-display font-bold">{r.party}</td>
                <td className="px-3 py-2">{r.pan || <span className="text-red-600 font-bold text-[10px]">PAN REQUIRED</span>}</td>
                <td className="px-3 py-2">{r.section}</td>
                <td className="px-3 py-2">{formatINR(r.gross_amount)}</td>
                <td className="px-3 py-2">{r.tds_rate != null ? `${r.tds_rate}%` : '—'}</td>
                <td className="px-3 py-2 font-bold">{formatINR(r.tds_amount)}</td>
                <td className="px-3 py-2 text-slate-500">{r.period}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
